/**
 * Shared API error helper (error contract v1, see
 * engineering-standards/error-handling-and-api-contract.md, Part 2.5).
 *
 * Use `toApiError` in every `catch` that needs to look at an API failure instead of
 * reading `err.response.data.errors[0]` by hand. It never throws, so it is safe to
 * call inside a `catch`. It understands the v1 envelope AND the legacy shapes the
 * backends still return during the deprecation window:
 *
 *   v1:      { code, message, details, request_id }
 *   legacy:  { error: "..." | {field: [..]} }, { errors: "..." | [..] | {field: [..]} },
 *            { message: "..." }, DRF { detail: "..." } and DRF field maps { field: [..] }
 *
 * Transports understood: axios errors (`e.response.data`), the legacy fetch wrapper
 * rejection `{ status, json }`, plain `Error`, strings, and anything else.
 *
 * Message order is deliberately the same order the apps used before
 * (errors, error, message, detail), so adopting the helper does not change what
 * users already see.
 */

export const GENERIC_ERROR_MESSAGE = "Something went wrong";

const CODE_BY_STATUS: Record<number, string> = {
  400: "bad_request",
  401: "unauthorized",
  402: "payment_required",
  403: "forbidden",
  404: "not_found",
  409: "conflict",
  422: "validation_failed",
  429: "rate_limited",
  502: "upstream_unavailable",
  503: "upstream_unavailable",
  504: "upstream_unavailable",
};

export class ApiError extends Error {
  /** HTTP status, or null for network errors / non-HTTP failures. */
  status: number | null;
  /** Envelope code, or one derived from the status, "network_error" or "unknown_error". */
  code: string;
  /** Envelope `details` (validation: { field: [messages] }), if any. */
  details: unknown;
  /** Value of the envelope `request_id` / `X-Request-Id` header, if any. */
  requestId: string | null;
  /** The original thrown value. For logging only; never show it to a user. */
  raw: unknown;

  constructor(init: {
    message: string;
    status?: number | null;
    code?: string;
    details?: unknown;
    requestId?: string | null;
    raw?: unknown;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.status = init.status ?? null;
    this.code = init.code ?? "unknown_error";
    this.details = init.details;
    this.requestId = init.requestId ?? null;
    this.raw = init.raw;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

type Dict = Record<string, unknown>;

const isDict = (value: unknown): value is Dict =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const nonEmptyString = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value : null;

/** Flattens a string / array / { field: [..] } value into one readable line, or null. */
function flatten(value: unknown): string | null {
  const text = nonEmptyString(value);
  if (text) return text;
  if (Array.isArray(value)) {
    const parts = value
      .map(flatten)
      .filter((part): part is string => part !== null);
    return parts.length ? parts.join(", ") : null;
  }
  if (isDict(value)) {
    const parts = Object.values(value)
      .map(flatten)
      .filter((part): part is string => part !== null);
    return parts.length ? parts.join(", ") : null;
  }
  return null;
}

const BODY_KEYS = [
  "code",
  "message",
  "details",
  "request_id",
  "error",
  "errors",
  "detail",
];

/**
 * True for a plain object that looks like an API error body passed directly (for
 * example a raw v1 envelope). Errors, axios errors and request wrappers are not bodies.
 */
function isBareBody(e: Dict): boolean {
  if (e instanceof Error || typeof e.stack === "string") return false;
  if (e.isAxiosError === true || "response" in e || "request" in e)
    return false;
  if (typeof e.status === "number") return false;
  return BODY_KEYS.some((key) => key in e);
}

/** Finds the response body: axios `response.data`, legacy fetch `json`, or the value itself. */
function bodyOf(e: unknown): unknown {
  if (!isDict(e)) return undefined;
  const response = e.response;
  if (isDict(response) && "data" in response) return response.data;
  if ("json" in e) return e.json;
  if ("data" in e && "status" in e) return e.data;
  if (isBareBody(e)) return e;
  return undefined;
}

function statusOf(e: unknown): number | null {
  if (!isDict(e)) return null;
  const response = e.response;
  const candidates = [isDict(response) ? response.status : undefined, e.status];
  for (const candidate of candidates) {
    if (typeof candidate === "number" && candidate >= 100 && candidate <= 599)
      return candidate;
  }
  return null;
}

function headerOf(e: unknown, name: string): string | null {
  if (!isDict(e) || !isDict(e.response)) return null;
  const headers = e.response.headers;
  if (!isDict(headers)) return null;
  const wanted = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === wanted) return nonEmptyString(headers[key]);
  }
  return null;
}

function messageFromBody(body: unknown): string | null {
  const text = nonEmptyString(body);
  if (text) return text;
  if (Array.isArray(body)) return flatten(body);
  if (!isDict(body)) return null;
  return (
    flatten(body.errors) ??
    flatten(body.error) ??
    nonEmptyString(body.message) ??
    nonEmptyString(body.detail) ??
    // DRF field map: { email: ["Enter a valid email."] } (no reserved keys at all)
    (!("code" in body) && !("details" in body) ? flatten(body) : null)
  );
}

/**
 * Converts anything that was thrown into an `ApiError`. Never throws.
 */
export function toApiError(e: unknown): ApiError {
  try {
    if (e instanceof ApiError) return e;

    const status = statusOf(e);
    const body = bodyOf(e);
    const bodyDict = isDict(body) ? body : null;

    const envelopeCode = bodyDict ? nonEmptyString(bodyDict.code) : null;
    const details =
      bodyDict && "details" in bodyDict ? bodyDict.details : undefined;
    const requestId =
      (bodyDict ? nonEmptyString(bodyDict.request_id) : null) ??
      headerOf(e, "x-request-id");

    // `instanceof Error` fails across realms (iframes, jsdom), so read `message` off any object.
    const own =
      isDict(e) || e instanceof Error
        ? nonEmptyString((e as Dict).message)
        : nonEmptyString(e);
    // Prefer what the server said; fall back to the thrown Error message, then generic.
    const message = messageFromBody(body) ?? own ?? GENERIC_ERROR_MESSAGE;

    let code = envelopeCode;
    if (!code && status !== null)
      code =
        CODE_BY_STATUS[status] ?? (status >= 500 ? "internal_error" : null);
    if (!code) {
      const isNetwork =
        isDict(e) &&
        (e.code === "ERR_NETWORK" ||
          (status === null && ("request" in e || e.isAxiosError === true)));
      code = isNetwork ? "network_error" : "unknown_error";
    }

    return new ApiError({ message, status, code, details, requestId, raw: e });
  } catch {
    // The helper must never throw: it runs inside catch blocks.
    return new ApiError({
      message: GENERIC_ERROR_MESSAGE,
      code: "unknown_error",
      raw: e,
    });
  }
}

/** Text that is safe to show to the user. Falls back to a generic message. */
export function errorMessage(
  e: unknown,
  fallback: string = GENERIC_ERROR_MESSAGE
): string {
  const { message } = toApiError(e);
  return message && message !== GENERIC_ERROR_MESSAGE ? message : fallback;
}
